// @flow
import * as React from 'react';
import { useMutation } from 'urql';
import BoostTrackingContext from '@ta/cpm.tracking';
import { Route } from '@ta/lithium.context';
import { TripTrackingImpression } from '@ta/trips.impressions';
import * as Types from '@ta/trips.trip-types';
import SaveSessionTrackingContext from './SaveSessionTrackingContext';
import OrganizeSessionTrackingContext from './OrganizeSessionTrackingContext';
import SponsorshipTrackingContext from './SponsorshipTrackingContext';
import LogInteraction from './LogInteraction.gql';

/**
 * Utilize the InteractionV4 interface to send interactions for trips.
 * See interaction-tracking/trips.yaml for type definitions
 */
type TripsBasicEvent = {|
  /** This is logically required but we want to shove this in there ourselves instead of making the caller do it.
   *  To that end, we call it not required and make sure we always use it.
   */
  puid?: string,
|};

export type TripsContext =
  | 'AttractionDetail'
  | 'AttractionList'
  | 'AttractionProductReview'
  | 'Forums'
  | 'HomeFeed'
  | 'HotelDetail'
  | 'HotelList'
  | 'Map'
  | 'MySaves'
  | 'ProfileFeed'
  | 'Recent'
  | 'RestaurantDetail'
  | 'RestaurantList'
  | 'ShowUserReview'
  | 'VacationRentalDetail'
  | 'VacationRentalList';

type TripsContextualEvent = {|
  ...TripsBasicEvent,
  context?: TripsContext,
|};

export type TripsEventSessionType = 'ORGANIZE' | 'SAVE';

export type TripsEventSession = {|
  sessionType?: TripsEventSessionType,
  sessionUuid?: string,
  sessionOrder?: number,
|};

type TripsOrganizeType = 'DAYS' | 'DATES' | 'NONE';

export type TripsOrganizeEvent = {|
  initialDuration?: number,
  endDuration?: number,
  initialOrganizeType?: TripsOrganizeType,
  endOrganizeType?: TripsOrganizeType,
|};

export type TripsElementClickActions =
  | 'openlocationsmodal'
  | 'openmodal'
  | 'todetail'
  | 'card_click';

type TripsRelation = 'OWNER' | 'VIEWER' | 'COLLABORATOR';

type TypedEventDetails = {|
  detailId?: string,
  saveType?: Types.ReferenceType,
  action?: TripsElementClickActions,
  tripId?: number,
  isBoosted?: boolean,
|};

type TripsTypedEvent = {|
  ...TripsContextualEvent,
  ...TripsOrganizeEvent,
  ...TypedEventDetails,
  ...TripsEventSession,
|};

export type TripsElement =
  | 'add_link'
  | 'add_mysave_to_trip'
  | 'addNoteCta'
  | 'attribution'
  | 'autoPopStat'
  | 'avatarCta'
  | 'browse'
  | 'book_now'
  | 'caption'
  | 'card_title'
  | 'card_media'
  | 'coverPhoto'
  | 'coverPhotoCta'
  | 'createTrip'
  | 'description'
  | 'detailCard'
  | 'dragCard'
  | 'empty_trip_tooltip'
  | 'editCoverPhoto'
  | 'expand'
  | 'expandNote'
  | 'forum_question_parent'
  | 'forum_question_text'
  | 'forum_reply_parent'
  | 'forum_reply_text'
  | 'inlineDatesCta'
  | 'invite'
  | 'item_like'
  | 'item_move'
  | 'item_overflow'
  | 'item_overflowReordering'
  | 'item_save'
  | 'item_save_login'
  | 'link_card'
  | 'location_information'
  | 'map'
  | 'mapCard'
  | 'mapCarousel'
  | 'mapPin'
  | 'mastheadCta'
  | 'mw_trips'
  | 'organizeCancel'
  | 'organizeAddItem'
  | 'organizeTypeDropdown'
  | 'organizeTypeUseDays'
  | 'organizeTypeUseDates'
  | 'photoUpload'
  | 'photo_carousel_change'
  | 'privacyIcon'
  | 'quickSave_change'
  | 'report'
  | 'resizePhoto'
  | 'review_content'
  | 'saved_viewTrip'
  | 'shareItem'
  | 'shareTrip'
  | 'social_stat_count'
  | 'stat'
  | 'stat_createTrip'
  | 'suggestedPhoto'
  | 'suggestedTripName'
  | 'trip_card'
  | 'trip_contributions'
  | 'trip_fab'
  | 'trip_fab_search'
  | 'trip_home_emptyCta'
  | 'trip_home_login'
  | 'trip_like'
  | 'trip_overflow'
  | 'trip_repost'
  | 'trip_save'
  | 'trip_search_modal_click'
  | 'trips_login_nav'
  | 'ugc_click_to_detail'
  | 'unsave_undo'
  | 'user_reference'
  | 'ITEM_SAVE_LOGIN'
  | 'trip_note_click'
  | 'add_trip_note'
  | 'trip_note_edit'
  | 'trip_note_delete';

type TripsElementClick = {|
  ...TripsTypedEvent,
  element: TripsElement,
  placeType?: Types.TripsPlaceType,
  relation?: TripsRelation,
|};

export type TripsFilterType = 'all' | 'private' | 'public' | 'my_saves';

type TripsFilterClick = {|
  ...TripsBasicEvent,
  element: TripsFilterType,
|};

type TripFields = 'title' | 'description' | 'coverPhoto';

export type TripsModalElement =
  | 'add_link'
  | 'comment_delete'
  | 'comment_edit'
  | 'createTrip'
  | 'editCoverPhoto'
  | 'editTrip'
  | 'item_save_login'
  | 'item_save_search_to_add'
  | 'item_undo_save_search_to_add'
  | 'organizeAddItem'
  | 'organizeTrip'
  | 'photoUpload'
  | 'privacy'
  | 'quickSave'
  | 'stat'
  | 'trip_home_login'
  | 'unsave'
  | 'addNoteCta'
  | 'trip_copy'
  | 'save_all'
  | 'ITEM_SAVE_LOGIN'
  | 'add_trip_note'
  | 'trip_note_edit';

type TripsModalSuccess = {|
  ...TripsTypedEvent,
  fields?: TripFields[],
  privacy?: Types.TripPrivacy,
  element: TripsModalElement,
  save_count?: ?number,
|};

export type TripsOverflowClickElement =
  | 'add_link'
  | 'collaborator_delete'
  | 'comment_delete'
  | 'comment_edit'
  | 'item_delete'
  | 'item_deleteReordering'
  | 'item_moveDown'
  | 'item_moveUp'
  | 'trip_collaborators'
  | 'trip_delete'
  | 'trip_edit'
  | 'trip_leave'
  | 'trip_organize'
  | 'trip_privacy'
  | 'trip_copy'
  | 'add_trip_note';

type TripsOverflowClick = {|
  ...TripsBasicEvent,
  ...TripsEventSession,
  ...TripsTypedEvent,
  element: TripsOverflowClickElement,
|};

type TripsLinkReferenceClick = {|
  ...TripsBasicEvent,
  authorUserId: string,
  url: string,
|};

export type TripsOverflowSuccessElement =
  | 'collaborator_delete'
  | 'item_delete'
  | 'trip_delete'
  | 'trip_leave';

type TripsOverflowSuccess = {|
  ...TripsBasicEvent,
  ...TripsOrganizeEvent,
  ...TypedEventDetails,
  ...TripsEventSession,
  element: TripsOverflowSuccessElement,
|};

type TripInteractionType = {|
  elementClick?: TripsElementClick,
  filterClick?: TripsFilterClick,
  modalSuccess?: TripsModalSuccess,
  overflowClick?: TripsOverflowClick,
  overflowSuccess?: TripsOverflowSuccess,
  linkReferenceClick?: TripsLinkReferenceClick,
|};

export type TripInteractionSources =
  | 'trips'
  | 'tripsHome'
  | 'tripsDetail'
  | 'tripsModal'
  | 'tripsMySaves';
export type LogInteractionFunction = (TripInteractionType, ?Types.TripSaveObject) => Promise<*>;

const servletMap = {
  Home: 'HomeFeed',
  Profile: 'ProfileFeed',
  Attractions: 'AttractionList',
  Attraction_Review: 'AttractionDetail',
  Attraction_Products: 'AttractionList',
  AttractionProductReview: 'AttractionProductReview',
  Hotels: 'HotelList',
  SmartDeals: 'HotelList',
  HotelHighlight: 'HotelList',
  HotelsList: 'HotelList',
  HotelsNear: 'HotelList',
  Vacation_Packages: 'HotelList',
  Hotel_Review: 'HotelDetail',
  Restaurants: 'RestaurantList',
  Restaurant_Review: 'RestaurantDetail',
  ShowUserReviews: 'ShowUserReview',
  ShowUserReviewsHotels: 'ShowUserReview',
  ShowUserReviewsRestaurants: 'ShowUserReview',
  ShowUserReviewsAttractions: 'ShowUserReview',
  Recent: 'Recent',
  Forums: 'Forums',
  ShowTopic: 'Forums',
  ShowForum: 'Forums',
  Trips: 'Trips',
  Map: 'Map',
  MySaves: 'MySaves',
  LastMinute: 'HotelList',
  LocalMaps: 'Map',
  LocalMapsRedesign: 'Map',
  VacationRentalReview: 'VacationRentalDetail',
};

const checkServletMap = (servlet: string) =>
  servlet && servletMap[servlet] ? servletMap[servlet].toUpperCase() : undefined;

export const getInteractionsContext = (servlet: string) =>
  servlet && (checkServletMap(servlet) || checkServletMap(servlet.replace(/^Mobile/, '')));

function fixCaseForGraphql(o: TripInteractionType): any {
  const interaction: any = o;
  ['elementClick', 'modalSuccess'].forEach(s => {
    if (interaction[s] && interaction[s].context) {
      interaction[s].context = getInteractionsContext(interaction[s].context) || null;
    }
    if (interaction[s] && interaction[s].saveType) {
      interaction[s].saveType = interaction[s].saveType.toUpperCase();
    }
    if (interaction[s] && interaction[s].action) {
      interaction[s].action = interaction[s].action.toUpperCase();
    }
  });
  ['elementClick', 'filterClick', 'modalSuccess', 'overflowClick', 'overflowSuccess'].forEach(e => {
    if (interaction[e] && interaction[e].element) {
      interaction[e].element = interaction[e].element.toUpperCase();
    }
  });
  ['modalSuccess'].forEach(e => {
    if (interaction[e] && interaction[e].fields) {
      interaction[e].fields = interaction[e].fields.map(field => field.toUpperCase());
    }
  });
  return interaction;
}

export type TripInteractionParams = {|
  source: TripInteractionSources,
  pageAsContext?: boolean,
  saveObject?: ?Types.TripSaveObject,
  /** The context to use for tracking. If pageAsContext is also specified, this value will be used. */
  trackingContext?: TripsContext,
  withSaveSession?: boolean,
  withOrganizeSession?: boolean,
  isBoosted?: boolean,
|};

export type TripInteractionProps = {|
  ...TripInteractionParams,
  children: (LogInteractionFunction, string) => React.Node,
|};

export const useTripInteraction = ({
  source,
  pageAsContext,
  saveObject,
  trackingContext,
  withSaveSession,
  withOrganizeSession,
}: TripInteractionParams): [LogInteractionFunction, string] => {
  const [{ page }] = React.useContext(Route);
  const {
    sessionUuid: saveSessionUuid,
    sessionOrder: saveSessionOrder,
    incrementOrder: incrementSaveSessionOrder,
  } = React.useContext(SaveSessionTrackingContext);
  const {
    sessionUuid: organizeSessionUuid,
    sessionOrder: organizeSessionOrder,
    incrementOrder: incrementOrganizeSessionOrder,
  } = React.useContext(OrganizeSessionTrackingContext);
  const trackingImpression = React.useContext(TripTrackingImpression);
  const puid = trackingImpression.impressionId || '';
  const context: any = trackingContext || (pageAsContext && page) || undefined;

  const { id: detailId, type: saveType } = saveObject || {};
  const { isBoosted } = React.useContext(BoostTrackingContext);
  const { tripId } = React.useContext(SponsorshipTrackingContext);
  const extraData: TypedEventDetails = React.useMemo(
    () => ({
      detailId: detailId ? `${detailId}` : undefined,
      saveType,
      isBoosted,
      tripId,
    }),
    [detailId, saveType, isBoosted, tripId],
  );

  const session: ?TripsEventSession = React.useMemo(() => {
    if (withSaveSession) {
      return {
        sessionType: 'SAVE',
        sessionUuid: saveSessionUuid,
        sessionOrder: saveSessionOrder,
      };
    } else if (withOrganizeSession) {
      return {
        sessionType: 'ORGANIZE',
        sessionUuid: organizeSessionUuid,
        sessionOrder: organizeSessionOrder,
      };
    }
    return null;
  }, [
    organizeSessionOrder,
    organizeSessionUuid,
    saveSessionOrder,
    saveSessionUuid,
    withOrganizeSession,
    withSaveSession,
  ]);

  const [, logInteraction] = useMutation(LogInteraction);
  const logInteractionFunction = React.useCallback(
    (data: TripInteractionType, tripSaveObject: ?Types.TripSaveObject) => {
      if (withSaveSession) {
        incrementSaveSessionOrder();
      } else if (withOrganizeSession) {
        incrementOrganizeSessionOrder();
      }

      let callbackExtra = extraData;
      if (tripSaveObject) {
        callbackExtra = {
          detailId: `${tripSaveObject.id}`,
          saveType: tripSaveObject.type,
        };
      }
      // eslint-disable-next-line no-restricted-globals
      const path = window.location.pathname;
      const pageContext =
        context === 'Unknown' ? path.match(/^\/([a-zA-Z0-9_]+)/)?.[1] || 'Home' : context;

      // flow is a big baby and won't let us do this in a loop.
      const tripInteraction: TripInteractionType = {
        elementClick: data.elementClick && {
          puid,
          ...callbackExtra,
          context: pageContext,
          ...session,
          ...data.elementClick,
        },
        filterClick: data.filterClick && { puid, ...data.filterClick },
        modalSuccess: data.modalSuccess && {
          puid,
          ...extraData,
          context: pageContext,
          ...session,
          ...data.modalSuccess,
        },
        overflowClick: data.overflowClick && { puid, ...data.overflowClick, ...session },
        overflowSuccess: data.overflowSuccess && {
          puid,
          ...session,
          ...extraData,
          ...data.overflowSuccess,
        },
        linkReferenceClick: data.linkReferenceClick && {
          puid,
          ...data.linkReferenceClick,
        },
      };

      /* $FlowFixGraphql variables are calculated at runtime for this call. Static validation fails on it. */
      return logInteraction({ interaction: { [source]: fixCaseForGraphql(tripInteraction) } });
    },
    [
      withSaveSession,
      withOrganizeSession,
      puid,
      extraData,
      context,
      session,
      logInteraction,
      source,
      incrementSaveSessionOrder,
      incrementOrganizeSessionOrder,
    ],
  );
  return [logInteractionFunction, puid];
};

const TripInteraction = ({
  children,
  source,
  pageAsContext,
  saveObject,
  trackingContext,
  withSaveSession,
  withOrganizeSession,
}: TripInteractionProps) => {
  const [logInteraction, puid] = useTripInteraction({
    source,
    pageAsContext,
    saveObject,
    trackingContext,
    withSaveSession,
    withOrganizeSession,
  });
  return children(logInteraction, puid);
};

TripInteraction.defaultProps = {
  pageAsContext: false,
  saveObject: undefined,
  trackingContext: undefined,
};

export default TripInteraction;
